// pages/Dashboard/Dashboard.tsx

import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './DashboardPage.module.scss';
import dashboardService from '../../services/dashboardService';
import {
  DashboardOverview,
  GraphData,
  StockStatus
} from '../../types/dashboardTypes';

const DashboardPage: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [graphs, setGraphs] = useState<GraphData | null>(null);
  const [stockStatus, setStockStatus] = useState<StockStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [graphPeriod, setGraphPeriod] = useState<number>(6);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [overviewRes, graphsRes, stockRes] = await Promise.all([
        dashboardService.getOverview({ month: selectedMonth, year: selectedYear }),
        dashboardService.getGraphs({ period: graphPeriod }),
        dashboardService.getStockStatus()
      ]);

    //   if (overviewRes.status) {
        setOverview(overviewRes.data);
    //   }

    //   if (graphsRes.status) {
        setGraphs(graphsRes.data);
    //   }

    //   if (stockRes.status) {
        setStockStatus(stockRes.data);
    //   }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedMonth, selectedYear, graphPeriod]);

  const formatCurrency = (value: number | string): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(num);
  };

  const getMonthName = (month: number): string => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1];
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loader}></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <p>Failed to load dashboard data</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
        <h4>Dashboard Overview</h4>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
          </div>
          <div className={styles.statLabel}>Total Invoices</div>
          <div className={styles.statValue}>{overview.invoices.total_count}</div>
          <div className={styles.statSubtext}>
            Total: {formatCurrency(overview.invoices.total_amount)}
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
          </div>
          <div className={styles.statLabel}>Received Amount</div>
          <div className={styles.statValue}>
            {formatCurrency(overview.invoices.received_amount)}
          </div>
          <div className={styles.statSubtext}>
            From {overview.invoices.total_count} invoices
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
          </div>
          <div className={styles.statLabel}>Balance Amount</div>
          <div className={styles.statValue}>
            {formatCurrency(overview.invoices.balance_amount)}
          </div>
          <div className={styles.statSubtext}>
            Outstanding payments
          </div>
        </div>

        <div className={`${styles.statCard} ${styles.profit}`}>
          <div className={styles.statHeader}>
            <div className={`${styles.statIcon} ${styles.profit}`}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="19" x2="12" y2="5"/>
                <polyline points="5 12 12 5 19 12"/>
              </svg>
            </div>
          </div>
          <div className={styles.statLabel}>Net Profit</div>
          <div className={styles.statValue}>
            {formatCurrency(overview.profit_loss.net_profit_loss)}
          </div>
          <div className={styles.statSubtext}>
            Profit: {formatCurrency(overview.profit_loss.total_profit)} | Loss: {formatCurrency(overview.profit_loss.total_loss)}
          </div>
        </div>
      </div>

      {/* Stock Status Summary */}
      {stockStatus && (
        <div className={styles.statsGrid}>
          <div className={`${styles.statCard} ${styles.stock}`}>
            <div className={styles.statHeader}>
              <div className={`${styles.statIcon} ${styles.stock}`}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                  <line x1="12" y1="22.08" x2="12" y2="12"/>
                </svg>
              </div>
            </div>
            <div className={styles.statLabel}>Total Products</div>
            <div className={styles.statValue}>{stockStatus.summary.total_products}</div>
            <div className={styles.statSubtext}>
              Stock Value: {formatCurrency(stockStatus.summary.total_stock_value)}
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.loss}`}>
            <div className={styles.statHeader}>
              <div className={`${styles.statIcon} ${styles.loss}`}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              </div>
            </div>
            <div className={styles.statLabel}>Critical Stock</div>
            <div className={styles.statValue}>{stockStatus.summary.critical_stock}</div>
            <div className={styles.statSubtext}>
              Requires immediate attention
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.stock}`}>
            <div className={styles.statHeader}>
              <div className={`${styles.statIcon} ${styles.stock}`}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
            </div>
            <div className={styles.statLabel}>Low Stock Items</div>
            <div className={styles.statValue}>{stockStatus.summary.low_stock}</div>
            <div className={styles.statSubtext}>
              Reorder soon
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.profit}`}>
            <div className={styles.statHeader}>
              <div className={`${styles.statIcon} ${styles.profit}`}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
            </div>
            <div className={styles.statLabel}>Normal Stock</div>
            <div className={styles.statValue}>{stockStatus.summary.normal_stock}</div>
            <div className={styles.statSubtext}>
              Adequate inventory
            </div>
          </div>
        </div>
      )}

      {/* Stock Status Grid */}
      {stockStatus && stockStatus.stocks.length > 0 && (
        <div className={styles.stockSection}>
          <h5 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600 }}>
            Stock Status
          </h5>
          <div className={styles.stockGrid}>
            {stockStatus.stocks
              .filter(stock => stock.alert_level !== 'normal')
              .map((stock) => (
                <div
                  key={stock.product_id}
                  className={`${styles.stockCard} ${styles[stock.alert_level]}`}
                >
                  <div className={styles.stockHeader}>
                    <div className={styles.stockName}>{stock.product_name}</div>
                    <span className={`${styles.alertBadge} ${styles[stock.alert_level]}`}>
                      {stock.alert_level}
                    </span>
                  </div>
                  <div className={styles.stockInfo}>
                    <div className={styles.stockCount}>
                      <span className={styles.label}>Full Stock</span>
                      <span className={styles.value}>{stock.full_stock}</span>
                    </div>
                    <div className={styles.stockCount}>
                      <span className={styles.label}>Empty Stock</span>
                      <span className={styles.value}>{stock.empty_stock}</span>
                    </div>
                    <div className={styles.stockCount}>
                      <span className={styles.label}>Value</span>
                      <span className={styles.value}>
                        {formatCurrency(stock.stock_value)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Profit/Loss by Product */}
      {overview.profit_loss.by_product.length > 0 && (
        <div className={styles.productTable}>
          <h5>Profit & Loss by Product</h5>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Purchased</th>
                <th>Purchase Cost</th>
                <th>Sold</th>
                <th>Sales Amount</th>
                <th>Profit/Loss</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {overview.profit_loss.by_product.map((product) => (
                <tr key={product.product_id}>
                  <td>{product.product_name}</td>
                  <td>{product.purchased}</td>
                  <td>{formatCurrency(product.purchase_cost)}</td>
                  <td>{product.sold}</td>
                  <td>{formatCurrency(product.sales_amount)}</td>
                  <td>{formatCurrency(product.profit_loss)}</td>
                  <td>
                    <span
                      className={
                        product.status === 'profit'
                          ? styles.profitBadge
                          : styles.lossBadge
                      }
                    >
                      {product.status.toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top Products */}
      {graphs && graphs.top_products.length > 0 && (
        <div className={styles.productTable}>
          <h5>Top Selling Products (Last {graphPeriod} Months)</h5>
          <table>
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Quantity Sold</th>
                <th>Sales Amount</th>
              </tr>
            </thead>
            <tbody>
              {graphs.top_products.map((product, index) => (
                <tr key={index}>
                  <td>{product.product_name}</td>
                  <td>{product.quantity_sold}</td>
                  <td>{formatCurrency(product.sales_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default DashboardPage;